import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, Check, X, ArrowLeft } from "lucide-react";
import { useSettingsContext } from "../../context/SettingsContext";
import {
  ROLES,
  INVITATION_RULES,
  USER_JOURNEY,
  FAQ,
  PAGE_CONTENT,
  PERMISSIONS_MATRIX,
  type Role,
  type InvitationRule,
} from "../../utils/rolesData";

const RolesPage: React.FC = () => {
  const navigate = useNavigate();
  const { setIsSettingsOpen, setActiveSettingsTab } = useSettingsContext();
  const [expandedRole, setExpandedRole] = useState<string | null>(null);
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  const handleBackToSettings = () => {
    // Navigate with state to indicate we want to open settings
    navigate("/", { state: { openSettings: true, settingsTab: "members" } });
  };

  const getRoleById = (roleId: string): Role | undefined => {
    return ROLES.find((r) => r.id === roleId);
  };

  return (
    <div className="min-h-screen bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark flex flex-col">
      {/* Simple Header */}
      <header className="border-b border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark">
        <div className="container mx-auto px-6 py-8 max-w-6xl">
          <button
            onClick={handleBackToSettings}
            className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition-colors mb-6 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            <span className="font-medium">Back to Settings</span>
          </button>
          <h1 className="text-3xl font-semibold text-text-primary dark:text-text-primaryDark mb-2">
            {PAGE_CONTENT.hero.title}
          </h1>
          <p className="text-base text-text-secondary dark:text-text-secondaryDark">
            {PAGE_CONTENT.hero.subtitle}
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8 max-w-6xl">
        <div className="space-y-16">
          {/* Role Hierarchy */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark mb-3">
                {PAGE_CONTENT.sections.hierarchy.title}
              </h2>
              <p className="text-base text-text-secondary dark:text-text-secondaryDark">
                {PAGE_CONTENT.sections.hierarchy.description}
              </p>
            </div>

            <div className="space-y-4">
              {ROLES.sort((a, b) => b.level - a.level).map((role, idx) => (
                <div key={role.id} className="relative">
                  {idx < ROLES.length - 1 && (
                    <div className="absolute left-6 top-12 w-0.5 h-8 bg-divider dark:bg-divider-dark"></div>
                  )}
                  <div
                    className="flex items-start gap-4 p-4 rounded-xl border"
                    style={{
                      borderColor: `${role.color}40`,
                      backgroundColor: `${role.color}08`,
                    }}
                  >
                    <div
                      className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
                      style={{ backgroundColor: `${role.color}20` }}
                    >
                      {role.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3
                          className="text-xl font-bold"
                          style={{ color: role.color }}
                        >
                          {role.name}
                        </h3>
                        <span className="text-xs px-2 py-1 rounded-full bg-background-panel dark:bg-background-panelDark text-text-secondary dark:text-text-secondaryDark">
                          Level {role.level}
                        </span>
                      </div>
                      <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                        {role.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Role Details */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark mb-3">
                {PAGE_CONTENT.sections.details.title}
              </h2>
              <p className="text-base text-text-secondary dark:text-text-secondaryDark">
                {PAGE_CONTENT.sections.details.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {ROLES.sort((a, b) => b.level - a.level).map((role) => (
                <div
                  key={role.id}
                  className="rounded-xl border border-divider dark:border-divider-dark overflow-hidden bg-background-panel dark:bg-background-panelDark/50"
                >
                  <div
                    className="p-6 border-b"
                    style={{ borderColor: `${role.color}30` }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-3xl">{role.icon}</span>
                      <div>
                        <h3
                          className="text-xl font-bold"
                          style={{ color: role.color }}
                        >
                          {role.name}
                        </h3>
                        <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                          {role.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 space-y-4">
                    {role.capabilities.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                          Capabilities
                        </h4>
                        <ul className="space-y-1.5">
                          {role.capabilities.map((capability, idx) => (
                            <li
                              key={idx}
                              className="flex items-start gap-2 text-sm text-text-secondary dark:text-text-secondaryDark"
                            >
                              <Check className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                              <span>{capability}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {role.limitations.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                          Limitations
                        </h4>
                        <ul className="space-y-1.5">
                          {role.limitations.map((limitation, idx) => (
                            <li
                              key={idx}
                              className="flex items-start gap-2 text-sm text-text-secondary dark:text-text-secondaryDark"
                            >
                              <X className="w-4 h-4 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
                              <span>{limitation}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Permissions Matrix */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark mb-3">
                {PAGE_CONTENT.sections.permissions.title}
              </h2>
              <p className="text-base text-text-secondary dark:text-text-secondaryDark">
                {PAGE_CONTENT.sections.permissions.description}
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-divider dark:border-divider-dark">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                      Permission
                    </th>
                    {ROLES.sort((a, b) => b.level - a.level).map((role) => (
                      <th
                        key={role.id}
                        className="text-center py-3 px-4 text-sm font-semibold"
                        style={{ color: role.color }}
                      >
                        <div className="flex flex-col items-center gap-1">
                          <span>{role.icon}</span>
                          <span>{role.name}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PERMISSIONS_MATRIX.permissions.map((permission) => {
                    return (
                      <tr
                        key={permission.id}
                        className="border-b border-divider dark:border-divider-dark hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors"
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span>{permission.icon}</span>
                            <span className="text-sm text-text-primary dark:text-text-primaryDark">
                              {permission.name}
                            </span>
                          </div>
                        </td>
                        {ROLES.sort((a, b) => b.level - a.level).map((role) => {
                          const roleData = PERMISSIONS_MATRIX.roles.find(
                            (r) => r.id === role.id
                          );
                          const hasPermission =
                            roleData?.permissions[permission.id] || false;
                          return (
                            <td key={role.id} className="py-3 px-4 text-center">
                              {hasPermission ? (
                                <Check className="w-5 h-5 text-secondary mx-auto" />
                              ) : (
                                <X className="w-5 h-5 text-red-500 dark:text-red-400 mx-auto opacity-50" />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Invitation Guide */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark mb-3">
                {PAGE_CONTENT.sections.invitations.title}
              </h2>
              <p className="text-base text-text-secondary dark:text-text-secondaryDark">
                {PAGE_CONTENT.sections.invitations.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {INVITATION_RULES.map((rule) => {
                const role = getRoleById(rule.inviterRole);
                if (!role) return null;

                return (
                  <div
                    key={rule.inviterRole}
                    className="rounded-xl border border-divider dark:border-divider-dark p-6 bg-background-panel dark:bg-background-panelDark/50"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-2xl">{role.icon}</span>
                      <h3
                        className="text-lg font-bold"
                        style={{ color: role.color }}
                      >
                        {role.name}
                      </h3>
                    </div>

                    {rule.canInvite.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                          Can Invite:
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {rule.canInvite.map((roleId) => {
                            const inviteRole = getRoleById(roleId);
                            if (!inviteRole) return null;
                            return (
                              <div
                                key={roleId}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm"
                                style={{
                                  backgroundColor: `${inviteRole.color}20`,
                                  color: inviteRole.color,
                                }}
                              >
                                <span>{inviteRole.icon}</span>
                                <span className="font-medium">
                                  {inviteRole.name}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {rule.cannotInvite.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                          Cannot Invite:
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {rule.cannotInvite.map((roleId) => {
                            const inviteRole = getRoleById(roleId);
                            if (!inviteRole) return null;
                            return (
                              <div
                                key={roleId}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm opacity-60"
                                style={{
                                  backgroundColor: `${inviteRole.color}10`,
                                  color: inviteRole.color,
                                }}
                              >
                                <X className="w-3 h-3" />
                                <span>{inviteRole.name}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-text-secondary dark:text-text-secondaryDark mt-4 pt-4 border-t border-divider dark:border-divider-dark">
                      {rule.explanation}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* User Journey */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark mb-3">
                {PAGE_CONTENT.sections.journey.title}
              </h2>
              <p className="text-base text-text-secondary dark:text-text-secondaryDark">
                {PAGE_CONTENT.sections.journey.description}
              </p>
            </div>

            <div className="space-y-4">
              {USER_JOURNEY.map((step, idx) => (
                <div key={step.step} className="relative">
                  {idx < USER_JOURNEY.length - 1 && (
                    <div className="absolute left-6 top-16 w-0.5 h-12 bg-divider dark:bg-divider-dark"></div>
                  )}
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                      <span className="text-lg font-bold text-secondary">
                        {step.step}
                      </span>
                    </div>
                    <div className="flex-1 pt-1">
                      <h3 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark mb-1">
                        {step.title}
                      </h3>
                      <p className="text-sm text-text-secondary dark:text-text-secondaryDark mb-2">
                        {step.description}
                      </p>
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-background-panel dark:bg-background-panelDark">
                        <span className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark">
                          Outcome:
                        </span>
                        <span className="text-xs text-text-primary dark:text-text-primaryDark">
                          {step.outcome}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark mb-3">
                {PAGE_CONTENT.sections.faq.title}
              </h2>
              <p className="text-base text-text-secondary dark:text-text-secondaryDark">
                {PAGE_CONTENT.sections.faq.description}
              </p>
            </div>

            <div className="space-y-3">
              {FAQ.map((item, idx) => (
                <div
                  key={idx}
                  className="rounded-lg border border-divider dark:border-divider-dark overflow-hidden bg-background-panel dark:bg-background-panelDark/50"
                >
                  <button
                    onClick={() =>
                      setExpandedFAQ(expandedFAQ === idx ? null : idx)
                    }
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-background-default dark:hover:bg-background-dark transition-colors"
                  >
                    <span className="font-semibold text-text-primary dark:text-text-primaryDark pr-4">
                      {item.question}
                    </span>
                    {expandedFAQ === idx ? (
                      <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-secondaryDark flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-secondaryDark flex-shrink-0" />
                    )}
                  </button>
                  {expandedFAQ === idx && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default RolesPage;

